import { Badge, Box, Checkbox, Flex, HStack, Table, Tbody, Text, Thead, Tr } from '@chakra-ui/react';
import { SettingsTd, SettingsTh } from './SettingsTable';
import { SETTINGS_BORDER_COLOR } from './constants';

export interface SettingsCheckRow {
    id: string;
    label: string;
    isChecked: boolean;
    isDisabled?: boolean;
    /** Small grey badge after the label, e.g. "Already added". */
    badge?: string;
    /** Renders bold on a tinted row, for an "All …" row above the rest. */
    isEmphasis?: boolean;
}

export interface SettingsCheckTableProps {
    /** Header for the name column. */
    columnLabel: string;
    /** Header for the checkbox column. */
    checkLabel: string;
    rows: SettingsCheckRow[];
    onToggle: (id: string, checked: boolean) => void;
    emptyMessage: string;
    /**
     * Fill the height available in the pane instead of capping at `maxH`. Use in
     * a flush pane, where this table is the only thing in it.
     */
    fill?: boolean;
    maxH?: string;
}

/**
 * Contained pick-list table: one row per option with a checkbox, for the many
 * places a settings screen asks the same yes/no question of a list — which
 * locations a carrier covers, which makes to include, which marketplaces count.
 * Rows with more than two states need a table of their own.
 *
 * The height follows the rows — capped by `maxH`, or by the pane when `fill` is
 * set — and the header stays pinned while the rows scroll under it.
 */
const SettingsCheckTable = ({
    columnLabel,
    checkLabel,
    rows,
    onToggle,
    emptyMessage,
    fill = false,
    maxH = '320px',
}: SettingsCheckTableProps) => (
    <Flex
        direction="column"
        minH={0}
        maxH={fill ? '100%' : undefined}
        bg="white"
        borderWidth="1px"
        borderColor={SETTINGS_BORDER_COLOR}
        borderRadius="lg"
        overflow="hidden"
    >
        <Box
            minH={0}
            maxH={fill ? undefined : maxH}
            overflow="auto"
            sx={{
                '& thead': { zIndex: 2 },
                '& th': {
                    bg: 'gray.50',
                    borderBottomWidth: '1px',
                    borderBottomColor: 'var(--chakra-colors-gray-200)',
                },
                '& tbody tr:last-of-type td': { borderBottomWidth: 0 },
                '& th:first-of-type, & td:first-of-type': { pl: 5 },
                '& th:last-of-type, & td:last-of-type': { pr: 5 },
                '&::-webkit-scrollbar': { width: '8px', height: '8px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': { background: '#cbd5e0', borderRadius: '4px' },
                scrollbarWidth: 'thin',
                scrollbarColor: '#cbd5e0 transparent',
            }}
        >
            <Table variant="simple" size="sm" w="100%" sx={{ tableLayout: 'fixed' }}>
                <Thead position="sticky" top={0}>
                    <Tr bg="gray.50">
                        <SettingsTh>{columnLabel}</SettingsTh>
                        {/* The checkboxes speak for themselves, so the column
                            carries no header; `checkLabel` still names them for
                            screen readers. */}
                        <SettingsTh w="110px" />
                    </Tr>
                </Thead>
                <Tbody>
                    {rows.length === 0 ? (
                        <Tr>
                            <SettingsTd colSpan={2} py={8} textAlign="center">
                                <Text fontSize="sm" color="gray.500">
                                    {emptyMessage}
                                </Text>
                            </SettingsTd>
                        </Tr>
                    ) : (
                        rows.map((row) => (
                            <Tr
                                key={row.id}
                                bg={row.isEmphasis ? 'gray.50' : undefined}
                                _hover={{ bg: row.isEmphasis ? 'gray.50' : 'gray.25' }}
                            >
                                <SettingsTd
                                    color={row.isDisabled ? 'gray.400' : 'gray.700'}
                                    fontWeight={row.isEmphasis ? 'semibold' : undefined}
                                >
                                    <HStack spacing={2} minW={0}>
                                        <Text fontSize="sm" noOfLines={1}>
                                            {row.label}
                                        </Text>
                                        {row.badge && (
                                            <Badge colorScheme="gray" fontSize="2xs" flexShrink={0}>
                                                {row.badge}
                                            </Badge>
                                        )}
                                    </HStack>
                                </SettingsTd>
                                <SettingsTd textAlign="center">
                                    <Flex justify="center">
                                        <Checkbox
                                            colorScheme="partbay-blue"
                                            isChecked={row.isChecked}
                                            isDisabled={row.isDisabled}
                                            onChange={(e) => onToggle(row.id, e.target.checked)}
                                            aria-label={`${checkLabel} — ${row.label}`}
                                        />
                                    </Flex>
                                </SettingsTd>
                            </Tr>
                        ))
                    )}
                </Tbody>
            </Table>
        </Box>
    </Flex>
);

export default SettingsCheckTable;
